import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useChat } from "../contexts/ChatContext";
import { useProposal } from "../contexts/ProposalContext";

// Components
import Layout from "../components/layout/Layout";
import ChatSidebar from "../components/chat/ChatSidebar";
import DocumentSidebar from "../components/chat/DocumentSidebar";
import MessageBubble from "../components/chat/MessageBubble";
import MessageInput from "../components/chat/MessageInput";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";
import PredefinedPromptsDropdown from "../components/chat/PredefinedPromptsDropdown";

const ChatInterface = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  const {
    currentSession,
    messages,
    loading,
    error,
    isTyping,
    fetchSession,
    sendMessage,
    createSession,
    clearCurrentSession,
    refreshSessions,
  } = useChat();

  const { proposals } = useProposal();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [documentSidebarOpen, setDocumentSidebarOpen] = useState(false);
  const [currentProposal, setCurrentProposal] = useState(null);
  const [referencedDocuments, setReferencedDocuments] = useState([]);

  // NEW: Track pending proposal ID for new chats
  const [pendingProposalId, setPendingProposalId] = useState(null);

  // Computed property to determine if sidebar should be open
  const shouldShowDocumentSidebar = Boolean(currentProposal);

  // Load session when sessionId changes
  useEffect(() => {
    if (sessionId) {
      fetchSession(parseInt(sessionId));
      // Clear pending proposal when switching to existing session
      setPendingProposalId(null);
    } else {
      clearCurrentSession();
    }
  }, [sessionId, fetchSession, clearCurrentSession]);

  // Find current proposal if session is proposal-specific
  useEffect(() => {
    console.log("=== PROPOSAL DETECTION useEffect ===");
    console.log(
      "currentSession:",
      currentSession?.session_name,
      "proposal_id:",
      currentSession?.proposal_id
    );
    console.log("proposals loaded:", proposals?.length);
    console.log("pendingProposalId:", pendingProposalId);

    let proposalToUse = null;

    // Check current session first
    if (currentSession && currentSession.proposal_id && proposals) {
      proposalToUse = proposals.find(
        (p) => p.proposal_id === currentSession.proposal_id
      );
    }
    // If no current session but we have a pending proposal, use that
    else if (pendingProposalId && proposals) {
      proposalToUse = proposals.find(
        (p) => p.proposal_id === pendingProposalId
      );
    }

    if (proposalToUse) {
      console.log("Found proposal:", proposalToUse.proposal_name);
      setCurrentProposal(proposalToUse);
      setDocumentSidebarOpen(true);
    } else {
      console.log("No proposal found, clearing current proposal");
      setCurrentProposal(null);
      // Only set to false if we're not in a proposal chat and no pending proposal
      if (!currentSession?.proposal_id && !pendingProposalId) {
        setDocumentSidebarOpen(false);
      }
    }
  }, [currentSession, proposals, pendingProposalId]);

  useEffect(() => {
    console.log("=== SESSION CHANGE EFFECT ===");
    if (currentSession) {
      if (
        currentSession.session_type === "proposal_specific" &&
        currentSession.proposal_id
      ) {
        console.log(
          "Session changed to proposal-specific, ensuring sidebar is open"
        );
        setDocumentSidebarOpen(true);
      } else {
        console.log("Session changed to general, closing sidebar");
        setDocumentSidebarOpen(false);
      }
    }
  }, [
    currentSession?.session_id,
    currentSession?.session_type,
    currentSession?.proposal_id,
  ]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async (message, options = {}) => {
    if (!currentSession) {
      // Determine which proposal to use - either from options or pending
      const proposalIdToUse = options.proposalId || pendingProposalId;

      const proposal =
        proposalIdToUse && proposals
          ? proposals.find((p) => p.proposal_id === proposalIdToUse)
          : null;

      const sessionName = proposal
        ? `${proposal.proposal_name} Chat`
        : "General Chat";

      console.log("Creating new session:", {
        sessionName,
        proposalId: proposalIdToUse,
        proposal: proposal?.proposal_name,
      });

      try {
        const newSession = await createSession(sessionName, proposalIdToUse);
        if (newSession) {
          // Navigate to the new session
          navigate(`/chat/${newSession.sessionId}`);

          // Clear pending proposal since we're creating the session
          setPendingProposalId(null);

          // Wait a moment for navigation to complete, then send the message
          setTimeout(async () => {
            await sendMessage(newSession.sessionId, message, {
              ...options,
              referencedDocuments: referencedDocuments.map(
                (doc) => doc.document_id
              ),
              referencedDocumentDetails: referencedDocuments,
            });

            // Refresh the sidebar to show the new session immediately
            if (refreshSessions) {
              await refreshSessions();
            }
          }, 100);
        }
        return;
      } catch (error) {
        console.error("Failed to create session:", error);
        return;
      }
    }

    // Send message to existing session
    await sendMessage(currentSession.session_id, message, {
      ...options,
      referencedDocuments: referencedDocuments.map((doc) => doc.document_id),
      referencedDocumentDetails: referencedDocuments,
    });
  };

  // UPDATED: Store the proposal ID for when user sends first message
  const handleNewChat = async (proposalId = null) => {
    console.log("handleNewChat called with proposalId:", proposalId);

    if (proposalId) {
      // Set the pending proposal ID so we know to create a proposal-specific chat
      setPendingProposalId(proposalId);
      console.log("Set pending proposal ID:", proposalId);
    } else {
      // Clear pending proposal for general chat
      setPendingProposalId(null);
    }

    // Navigate to empty chat interface
    // The session will be created when the user sends their first message
    navigate("/chat");
  };

  const handleReferenceDocument = (document) => {
    // Check if document is already referenced
    const isAlreadyReferenced = referencedDocuments.some(
      (doc) => doc.document_id === document.document_id
    );

    if (isAlreadyReferenced) {
      // Remove from references
      setReferencedDocuments((prev) =>
        prev.filter((doc) => doc.document_id !== document.document_id)
      );
    } else {
      // Add to references
      setReferencedDocuments((prev) => [...prev, document]);
    }
  };

  const handleRemoveReference = (documentId) => {
    setReferencedDocuments((prev) =>
      prev.filter((doc) => doc.document_id !== documentId)
    );
  };

  // Handle document sidebar toggle with proposal chat protection
  const handleDocumentSidebarToggle = () => {
    if (shouldShowDocumentSidebar) {
      // Don't allow closing sidebar for proposal chats
      console.log(
        "Cannot close document sidebar in proposal chat - ignoring toggle"
      );
      return;
    }
    console.log("Toggling document sidebar for non-proposal chat");
    setDocumentSidebarOpen(!documentSidebarOpen);
  };

  // NEW: Handle predefined prompt selection
  const handleSelectPredefinedPrompt = (prompt) => {
    // We'll pass this prompt to the MessageInput component
    // For now, we can trigger an event or use a ref to set the message
    // The simplest approach is to create a custom event
    const event = new CustomEvent("predefinedPromptSelected", {
      detail: { prompt },
    });
    window.dispatchEvent(event);
  };

  // Clear references when switching sessions
  useEffect(() => {
    setReferencedDocuments([]);
  }, [currentSession?.session_id]);

  const getContextInfo = () => {
    // Use current proposal (from session or pending)
    const proposalToShow = currentProposal;

    if (proposalToShow) {
      return {
        type: "proposal",
        name: proposalToShow.proposal_name,
        customer: proposalToShow.customer_name,
        icon: "file",
      };
    }
    return {
      type: "general",
      name: "General Assistant",
      description: "Ask me anything about proposal development",
      icon: "robot",
    };
  };

  const contextInfo = getContextInfo();

  return (
    <Layout title={currentSession?.session_name || "Chat with AI"}>
      <div className="flex h-[calc(100vh-8rem)] bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden w-full">
        {/* Chat Sidebar */}
        <ChatSidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onNewChat={handleNewChat}
          currentSessionId={currentSession?.session_id}
        />

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Chat Header */}
          <div className="border-b border-slate-200 p-4 bg-slate-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* Mobile sidebar toggle */}
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="lg:hidden p-2 rounded-lg hover:bg-slate-200 transition-colors duration-200"
                >
                  <FontAwesomeIcon icon="bars" className="text-slate-600" />
                </button>

                {/* Context Info */}
                <div className="flex items-center space-x-3">
                  <div
                    className={`p-2 rounded-lg ${
                      contextInfo.type === "proposal"
                        ? "bg-primary-100 text-primary-600"
                        : "bg-emerald-100 text-emerald-600"
                    }`}
                  >
                    <FontAwesomeIcon icon={contextInfo.icon} />
                  </div>
                  <div>
                    <h2 className="font-semibold text-slate-900">
                      {contextInfo.name}
                    </h2>
                    <p className="text-sm text-slate-600">
                      {contextInfo.customer || contextInfo.description}
                    </p>
                    {/* DEBUG: Show pending proposal info */}
                    {pendingProposalId && !currentSession && (
                      <p className="text-xs text-blue-600">
                        Ready for proposal chat
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Header Actions */}
              <div className="flex items-center space-x-2">
                {/* Predefined Prompts Dropdown */}
                <PredefinedPromptsDropdown
                  onSelectPrompt={handleSelectPredefinedPrompt}
                  disabled={loading || isTyping}
                />
              </div>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {loading && !currentSession ? (
              <div className="flex items-center justify-center h-full">
                <LoadingSpinner size="lg" text="Loading conversation..." />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full">
                <ErrorMessage message={error} />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center max-w-md">
                  <div className="mb-6">
                    <FontAwesomeIcon
                      icon="robot"
                      className="text-6xl text-slate-300 mb-4"
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    {contextInfo.type === "proposal"
                      ? `Ready to help with ${contextInfo.name}`
                      : "How can I help you today?"}
                  </h3>
                  <p className="text-slate-600 mb-6">
                    {contextInfo.type === "proposal"
                      ? "I have access to all documents and context for this proposal. Ask me anything!"
                      : "Ask me about proposal development, past performance, or general questions."}
                  </p>
                  <div className="space-y-2 text-sm text-slate-500">
                    <p>💡 Try asking:</p>
                    <ul className="space-y-1">
                      {contextInfo.type === "proposal" ? (
                        <>
                          <li>"What are the key evaluation criteria?"</li>
                          <li>"Help me write the technical approach"</li>
                          <li>"What past performance is most relevant?"</li>
                        </>
                      ) : (
                        <>
                          <li>"What makes a winning proposal?"</li>
                          <li>"How do I analyze an RFP effectively?"</li>
                          <li>"Show me our past similar projects"</li>
                        </>
                      )}
                    </ul>
                  </div>

                  {/* Show document hint for proposal chats */}
                  {contextInfo.type === "proposal" && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-700">
                        💡 Use Quick Prompts above or the documents panel to get
                        specific help with your proposal
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <>
                {messages.map((message, index) => (
                  <MessageBubble
                    key={message.message_id || index}
                    message={message}
                    isLatest={index === messages.length - 1}
                  />
                ))}

                {isTyping && (
                  <div className="flex items-center space-x-3 text-slate-500">
                    <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <FontAwesomeIcon
                        icon="robot"
                        className="text-primary-600 text-sm"
                      />
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                      <span className="text-sm">AI is thinking...</span>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Message Input */}
          <div className="border-t border-slate-200 p-4 bg-slate-50">
            <MessageInput
              onSendMessage={handleSendMessage}
              disabled={loading || isTyping}
              placeholder={
                contextInfo.type === "proposal"
                  ? `Ask about ${contextInfo.name}...`
                  : "Ask me anything about proposals..."
              }
              referencedDocuments={referencedDocuments}
              onRemoveReference={handleRemoveReference}
            />
          </div>
        </div>

        {/* Document Sidebar - show for proposal chats or when pending proposal */}
        {(shouldShowDocumentSidebar || pendingProposalId) &&
          currentProposal && (
            <DocumentSidebar
              isOpen={documentSidebarOpen}
              onClose={() => {
                console.log(
                  "DocumentSidebar onClose called - should not close for proposal chats"
                );
                // Don't actually close for proposal chats
                if (!shouldShowDocumentSidebar && !pendingProposalId) {
                  setDocumentSidebarOpen(false);
                }
              }}
              proposalId={currentProposal.proposal_id}
              onReferenceDocument={handleReferenceDocument}
              referencedDocuments={referencedDocuments}
            />
          )}
      </div>

      {/* Typing Indicator Styles */}
      <style jsx>{`
        .typing-indicator {
          display: flex;
          space-x: 2px;
        }

        .typing-indicator span {
          height: 4px;
          width: 4px;
          background-color: #64748b;
          border-radius: 50%;
          display: inline-block;
          animation: typing 1.4s ease-in-out infinite both;
          margin-right: 2px;
        }

        .typing-indicator span:nth-child(1) {
          animation-delay: -0.32s;
        }

        .typing-indicator span:nth-child(2) {
          animation-delay: -0.16s;
        }

        @keyframes typing {
          0%,
          80%,
          100% {
            transform: scale(0.8);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </Layout>
  );
};

export default ChatInterface;
